/** PDF AcroForm radio helpers: parent group name, member identity, and fill. */

export type RadioFieldLike = {
  name: string
  acroName?: string
  type?: string
  options?: string[]
  radioGroup?: string
  optionValue?: string
}

export type AutoRadioGroup = {
  id: string
  name: string
  fieldNames: string[]
  children: Array<{ type: 'field'; fieldName: string }>
}

export function stripUndefinedPdfName(raw: string): string {
  return String(raw ?? '')
    .trim()
    .replace(/\.undefined$/i, '')
    .replace(/\s*undefined$/i, '')
}

export function radioGroupNameFromStoredId(id: string): string {
  let s = String(id || '').trim()
  s = s.replace(/__opt_\d+(?:__dup_\d+)?$/i, '')
  const cut = s.indexOf('::')
  if (cut > 0) s = s.slice(0, cut)
  return stripUndefinedPdfName(s) || String(id || '').trim()
}

export function radioOptionFromQuestion(q: {
  pdfField?: { option?: string; id?: string }
  options?: string[]
  label?: string
}): string {
  const opt = q?.pdfField?.option ?? q?.options?.[0] ?? q?.label
  return String(opt ?? '').trim()
}

function decodePdfText(obj: any): string | null {
  if (!obj) return null
  try {
    if (typeof obj.decodeText === 'function') {
      const s = String(obj.decodeText() || '').trim()
      if (s && s.toLowerCase() !== 'undefined') return s
    }
  } catch {
    /* ignore */
  }
  return null
}

function readDictT(dict: any, PDFName: any): string | null {
  if (!dict || !PDFName) return null
  try {
    const t = typeof dict.lookup === 'function'
      ? dict.lookup(PDFName.of('T'))
      : dict.get?.(PDFName.of('T'))
    return decodePdfText(t)
  } catch {
    return null
  }
}

/** Walk Parent /T — Acrobat's radio group name. */
export function resolveRadioGroupName(field: any, PDFName: any): string {
  const dict = field?.acroField?.dict
  let parentT: string | null = null
  let cur = dict
  for (let i = 0; i < 8 && cur; i++) {
    try {
      const parent = typeof cur.lookup === 'function'
        ? cur.lookup(PDFName.of('Parent'))
        : cur.get?.(PDFName.of('Parent'))
      if (!parent) break
      const t = readDictT(parent, PDFName)
      if (t) parentT = t
      cur = parent
    } catch {
      break
    }
  }
  if (parentT) return parentT
  const ownT = readDictT(dict, PDFName)
  if (ownT) return ownT
  const fromGetName = stripUndefinedPdfName(String(field?.getName?.() || ''))
  return fromGetName || String(field?.getName?.() || '').trim()
}

export function makeRadioGroupId(groupName: string): string {
  const safe = String(groupName || 'Radio')
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_-]/g, '_')
  return `radio:${safe || 'Radio'}`
}

export function makeRadioMemberName(groupName: string, optionValue: string): string {
  const g = String(groupName || 'Radio').trim() || 'Radio'
  const opt = String(optionValue || '').trim() || 'option'
  return `${g}::${opt}`
}

export function buildAutoRadioGroupsFromFields(fields: RadioFieldLike[]): AutoRadioGroup[] {
  const byGroup = new Map<string, string[]>()
  for (const f of fields || []) {
    const groupName = String(f.radioGroup || '').trim()
    if (!groupName) continue
    const list = byGroup.get(groupName)
    if (list) list.push(f.name)
    else byGroup.set(groupName, [f.name])
  }
  return Array.from(byGroup.entries()).map(([groupName, fieldNames]) => ({
    id: makeRadioGroupId(groupName),
    name: groupName,
    fieldNames,
    children: fieldNames.map((fieldName) => ({ type: 'field' as const, fieldName })),
  }))
}

function normalizeKey(raw: string): string {
  return stripUndefinedPdfName(raw).replace(/\s+/g, ' ').trim().toLowerCase()
}

export function collectRadioMembersForGroup(
  acroFields: any[],
  groupName: string,
  PDFRadioGroup: any,
  PDFName: any,
): any[] {
  const wanted = normalizeKey(radioGroupNameFromStoredId(groupName) || groupName)
  if (!wanted) return []
  const members: any[] = []
  for (const f of acroFields || []) {
    if (!(f instanceof PDFRadioGroup)) continue
    const gn = normalizeKey(resolveRadioGroupName(f, PDFName) || String(f.getName?.() || ''))
    if (gn === wanted) members.push(f)
  }
  return members
}

/**
 * Select `optionValue` on the matching radio member and turn Off siblings.
 * Needed because Acrobat often exposes each button as its own PDFRadioGroup
 * named "Group.undefined" instead of one field named "Group".
 */
export function applyRadioGroupValue(
  acroFields: any[],
  args: {
    groupName: string
    optionValue: string
    PDFRadioGroup: any
    PDFName: any
  },
): { applied: boolean; memberCount: number } {
  const members = collectRadioMembersForGroup(
    acroFields,
    args.groupName,
    args.PDFRadioGroup,
    args.PDFName,
  )
  if (!members.length) return { applied: false, memberCount: 0 }

  const wanted = String(args.optionValue ?? '').trim()
  for (const m of members) {
    try {
      m.clear()
    } catch {
      /* ignore */
    }
  }
  if (!wanted) return { applied: true, memberCount: members.length }

  let selected = false
  for (const m of members) {
    let opts: string[] = []
    try {
      opts = (m.getOptions?.() || []).map((o: any) => String(o))
    } catch {
      opts = []
    }
    if (!opts.includes(wanted)) continue
    try {
      m.select(wanted)
      selected = true
    } catch {
      /* ignore */
    }
  }
  return { applied: selected, memberCount: members.length }
}

/**
 * pdf-lib's flatten() only picks On/Off streams when /AP /N is a dict.
 * Sejda stores that dict behind a PDFRef, so flatten bakes the dict itself
 * and checkboxes/radios disappear. Regenerating appearances inlines the dict.
 */
export function refreshButtonAppearances(
  acroFields: any[],
  classes: { PDFCheckBox: any; PDFRadioGroup: any },
): void {
  for (const f of acroFields || []) {
    const isButton =
      (classes.PDFCheckBox && f instanceof classes.PDFCheckBox) ||
      (classes.PDFRadioGroup && f instanceof classes.PDFRadioGroup)
    if (!isButton || typeof f.updateAppearances !== 'function') continue
    try {
      f.updateAppearances()
    } catch {
      // Keep fill moving; flatten/save still have fallbacks.
    }
  }
}
